import m from "mithril";
import { getMithril, showToast, formatRupiah, exportToXLSX, toExcelNumber } from "../../../utils.js";

const EquityChange = {
    loading: false,

    // Default to current month
    startDate: (() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    })(),
    endDate: (() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    })(),

    data: {
        modalAwal: 0,
        labaBersih: 0,
        mutasiEkuitas: [],
        totalMutasi: 0,
        modalAkhir: 0,
        period: {
            startDate: '',
            endDate: ''
        }
    },

    oninit: function (vnode) {
        this.fetchData();
    },

    fetchData: async function () {
        this.loading = true;
        m.redraw();

        try {
            const url = `/api/accounting/reports/equity-change?startDate=${this.startDate}&endDate=${this.endDate}`;
            const response = await getMithril(url, { component: this });

            if (response.success) {
                this.data = response.data;
            } else {
                showToast(response.message || "Gagal memuat Perubahan Ekuitas", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Terjadi kesalahan saat memuat Perubahan Ekuitas", "error");
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    exportCSV: function () {
        if (!this.data) {
            showToast("Tidak ada data untuk diekspor", "warning");
            return;
        }

        const headers = ["Keterangan", "Jumlah"];
        const rows = [];

        // Numeric cells must be JS Numbers so Excel stores them as numbers (not text).
        const toNum = (val) => toExcelNumber(val);

        rows.push(["Modal Awal", toNum(this.data.modalAwal)]);
        rows.push(["Laba / (Rugi) Bersih Periode Berjalan", toNum(this.data.labaBersih)]);

        if (this.data.mutasiEkuitas && this.data.mutasiEkuitas.length > 0) {
            rows.push(["MUTASI EKUITAS", ""]);
            this.data.mutasiEkuitas.forEach(acc => {
                rows.push([acc.custom_name || acc.coa_name, toNum(acc.balance)]);
            });
            rows.push(["Total Mutasi Ekuitas", toNum(this.data.totalMutasi)]);
        }

        rows.push(["", ""]);
        rows.push(["MODAL AKHIR", toNum(this.data.modalAkhir)]);

        exportToXLSX(`Perubahan_Ekuitas_${this.startDate}_sd_${this.endDate}`, headers, rows);
    },

    renderMutasiRows: function (mutasi) {
        if (!mutasi || mutasi.length === 0) {
            return m("tr", [
                m("td", { class: "px-6 py-4 text-sm text-gray-500 italic pl-12" }, "Tidak ada mutasi ekuitas pada periode ini"),
                m("td", { class: "px-6 py-4 text-sm text-gray-900 text-right font-mono" }, "-")
            ]);
        }

        return mutasi.map(acc => m("tr", { class: "border-b border-gray-100 hover:bg-gray-50/50" }, [
            m("td", { class: "px-6 py-2 text-sm text-gray-700 pl-12" },
                acc.custom_name || acc.coa_name
            ),
            m("td", { class: "px-6 py-2 text-sm text-gray-900 text-right font-mono" },
                formatRupiah(acc.balance)
            )
        ]));
    },

    view: function (vnode) {
        return m("div", { class: "container mx-auto px-4 py-6 max-w-5xl animate-fade-in" }, [

            // Header Section
            m("div", { class: "mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                m("div", { class: "flex flex-col md:flex-row md:items-center justify-between gap-4" }, [
                    m("div", [
                        m("h1", { class: "text-2xl font-bold tracking-tight text-gray-900" }, "Laporan Perubahan Ekuitas"),
                        m("p", { class: "mt-1 text-sm text-gray-500" }, "Statement of Changes in Equity")
                    ]),

                    m("div", { class: "flex flex-wrap items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" }, [
                        m("div", { class: "flex items-center gap-2" }, [
                            m("label", { class: "text-xs font-medium text-gray-500 ml-1" }, "Dari:"),
                            m("input", {
                                type: "date",
                                class: "px-3 py-1.5 bg-white text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                                value: this.startDate,
                                onchange: (e) => {
                                    this.startDate = e.target.value;
                                    this.fetchData();
                                }
                            })
                        ]),
                        m("span", { class: "text-gray-400" }, "-"),
                        m("div", { class: "flex items-center gap-2" }, [
                            m("label", { class: "text-xs font-medium text-gray-500 ml-1" }, "Sampai:"),
                            m("input", {
                                type: "date",
                                class: "px-3 py-1.5 bg-white text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                                value: this.endDate,
                                onchange: (e) => {
                                    this.endDate = e.target.value;
                                    this.fetchData();
                                }
                            })
                        ]),
                        m("button", {
                            class: "inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors ml-2",
                            onclick: () => this.exportCSV(),
                            title: "Ekspor ke CSV"
                        }, [
                            m("i", { class: "ri-file-download-line mr-2" }),
                            "Ekspor XLSX"
                        ])
                    ])
                ])
            ]),

            // Main Content Area
            this.loading
                ? m("div", { class: "flex justify-center items-center py-20" }, [
                    m("div", { class: "animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" })
                ])
                : m("div", { class: "bg-white shadow-md rounded-xl border border-gray-200 overflow-hidden" }, [

                    // Report Header
                    m("div", { class: "text-center py-8 border-b border-gray-200 bg-gray-50/50" }, [
                        m("h2", { class: "text-2xl font-bold text-gray-900 tracking-tight" }, "LAPORAN PERUBAHAN EKUITAS"),
                        m("p", { class: "text-sm text-gray-600 mt-2 font-medium" },
                            `Periode: ${new Date(this.data.period.startDate).toLocaleDateString()} s/d ${new Date(this.data.period.endDate).toLocaleDateString()}`
                        )
                    ]),

                    // Content Section
                    m("div", { class: "p-0" }, [
                        m("table", { class: "min-w-full divide-y divide-gray-200" }, [
                            m("tbody", { class: "bg-white divide-y divide-gray-100" }, [

                                // Modal Awal (Beginning Balance)
                                m("tr", { class: "bg-indigo-50/30" }, [
                                    m("td", { class: "px-6 py-5 text-base font-semibold text-gray-900" }, "Modal Awal"),
                                    m("td", { class: "px-6 py-5 text-base font-bold text-gray-900 text-right font-mono" },
                                        formatRupiah(this.data.modalAwal)
                                    )
                                ]),

                                // Laba / Rugi Bersih (Net Income / Loss)
                                m("tr", [
                                    m("td", { class: "px-6 py-4 text-sm font-medium text-gray-800 flex items-center gap-2" }, [
                                        m("span", { class: `w-2 h-2 rounded-full ${this.data.labaBersih >= 0 ? 'bg-green-500' : 'bg-red-500'}` }),
                                        "Laba / (Rugi) Bersih Periode Berjalan"
                                    ]),
                                    m("td", { class: `px-6 py-4 text-sm font-medium text-right font-mono ${this.data.labaBersih >= 0 ? 'text-green-600' : 'text-red-600'}` },
                                        formatRupiah(this.data.labaBersih)
                                    )
                                ]),

                                // Header for Mutasi Ekuitas
                                m("tr", { class: "bg-gray-50/50" }, [
                                    m("td", { class: "px-6 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider", colspan: "2" }, "Mutasi Ekuitas (Penambahan / Pengurangan)")
                                ]),

                                // List of Mutasi (Prive, etc.)
                                this.renderMutasiRows(this.data.mutasiEkuitas),

                                // Subtotal Mutasi
                                this.data.mutasiEkuitas.length > 0 ? m("tr", { class: "bg-gray-50/30" }, [
                                    m("td", { class: "px-6 py-3 text-sm font-medium text-gray-700 text-right pr-8" }, "Total Mutasi Ekuitas"),
                                    m("td", { class: "px-6 py-3 text-sm font-semibold text-gray-900 text-right font-mono border-t border-gray-200" },
                                        formatRupiah(this.data.totalMutasi)
                                    )
                                ]) : null,

                            ])
                        ])
                    ]),

                    // Final Summary Row (Modal Akhir)
                    m("div", {
                        class: "p-6 flex flex-col sm:flex-row justify-between items-center border-t-2 bg-indigo-50 border-indigo-200"
                    }, [
                        m("div", [
                            m("span", { class: "font-bold uppercase text-lg text-indigo-900 tracking-wide" }, "Modal Akhir"),
                            m("p", { class: "text-sm mt-1 text-indigo-700" }, "Saldo akhir ekuitas per tanggal laporan")
                        ]),
                        m("div", { class: "mt-4 sm:mt-0 px-6 py-4 bg-white rounded-xl shadow-sm border border-indigo-100" }, [
                            m("span", { class: "font-bold text-2xl font-mono text-indigo-700" },
                                formatRupiah(this.data.modalAkhir)
                            )
                        ])
                    ])
                ])
        ]);
    }
};

export default EquityChange;
